import { Boxes, ExternalLink } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';

import { ErrorNotice, PanelCard } from './primitives';
import { Skeleton } from '@/components/ui/skeleton';
import { useNow } from '@/hooks/useChainData';
import {
  CHAINS,
  formatBytes,
  formatInt,
  timeAgo,
  type BlockInfo,
  type ChainId,
} from '@/lib/mempool';

const BLOCKS_SHOWN = 8;

function BlockRow({ block, chainId, now }: { block: BlockInfo; chainId: ChainId; now: number }) {
  const chain = CHAINS[chainId];
  const pool = block.extras?.pool?.name;

  return (
    <a
      href={`${chain.explorerBase}/block/${block.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-accent/60 focus-visible:bg-accent/60 focus-visible:outline-none"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`font-mono text-sm font-semibold ${chain.classes.text}`}>
            #{formatInt(block.height)}
          </span>
          {pool && (
            <span className="truncate rounded border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {pool}
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
          {block.id.slice(0, 20)}…
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="font-mono text-xs">
          {formatInt(block.tx_count)} tx · {formatBytes(block.size)}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">{timeAgo(block.timestamp, now)}</div>
      </div>
      <ExternalLink className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </a>
  );
}

export function BlocksCard({
  chainId,
  query,
}: {
  chainId: ChainId;
  query: UseQueryResult<BlockInfo[]>;
}) {
  const chain = CHAINS[chainId];
  const { data, isPending, isError, error } = query;
  const now = useNow();

  return (
    <PanelCard
      icon={<Boxes className={`size-4 ${chain.classes.text}`} />}
      title="Latest Blocks"
      action={
        <a
          href={`${chain.explorerBase}/blocks`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          View all
          <ExternalLink className="size-3" />
        </a>
      }
    >
      {isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex items-center justify-between gap-3">
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-40" />
              </div>
              <div className="space-y-1.5 text-right">
                <Skeleton className="ml-auto h-3.5 w-28" />
                <Skeleton className="ml-auto h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <ErrorNotice message={error.message} />
      ) : (
        <div className="-my-1.5 divide-y divide-border/60">
          {data.slice(0, BLOCKS_SHOWN).map((block) => (
            <BlockRow key={block.id} block={block} chainId={chainId} now={now} />
          ))}
        </div>
      )}
    </PanelCard>
  );
}
